/*
 * Tech-53 customisation v2.3
 * - 2026-05-03 Migration vers Horizon (version récente) + fix bug checkbox terms-agree (DOM morphing race condition)
 * - 2026-05-03 Loading spinner on Checkout button while cart syncs (Horizon AJAX + Magical Fees recalc latency)
 *
 * Forces the customer to agree to the Terms & Conditions before checkout.
 * Robust against Horizon's cart-summary morphing, which previously reset the checkbox on first checkout click.
 *
 * Strategy:
 *   - Persist agreed state in sessionStorage (with try/catch fallback to in-memory state for Safari ITP / private mode).
 *   - Use ONE delegated listener per event type at document level (avoids the duplicate-listener pile-up of v1.0).
 *   - Capture-phase listeners run BEFORE Horizon's cart-form submit handler, so we can preempt navigation.
 *   - Sync the HTML `checked` attribute (not just the JS property) so morphing preserves visual state.
 *   - Reapply state after cart events (cart:refresh, cart:updated, etc.) and via a TARGETED MutationObserver
 *     scoped to cart containers only — never on document.documentElement.
 *   - Reset state at page load if cart is empty (read from .cart-bubble visibility, no AJAX).
 */
(function () {
  'use strict';

  const STORAGE_KEY = 'tech53.cart.termsAgreed';
  let memoryState = false;
  let storageWorks = true;

  try {
    sessionStorage.setItem('__t53_probe__', '1');
    sessionStorage.removeItem('__t53_probe__');
  } catch (_) {
    storageWorks = false;
  }

  function getAgreed() {
    if (storageWorks) {
      try {
        return sessionStorage.getItem(STORAGE_KEY) === '1';
      } catch (_) {
        storageWorks = false;
      }
    }
    return memoryState;
  }

  function setAgreed(v) {
    memoryState = !!v;
    if (storageWorks) {
      try {
        sessionStorage.setItem(STORAGE_KEY, v ? '1' : '0');
      } catch (_) {
        storageWorks = false;
      }
    }
  }

  function clearAgreed() {
    memoryState = false;
    if (storageWorks) {
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (_) {
        storageWorks = false;
      }
    }
  }

  // Empty-cart detection without AJAX — reads the visibility class set by Liquid in cart-bubble.liquid
  // (`<div class="cart-bubble{% if cart == empty %} visually-hidden{% endif %}">`).
  function isCartEmpty() {
    const bubble = document.querySelector('.cart-bubble');
    if (bubble) return bubble.classList.contains('visually-hidden');
    const countEl = document.querySelector('[ref="cartBubbleCount"], .cart-bubble__text-count');
    if (countEl) {
      const n = parseInt((countEl.textContent || '').trim(), 10);
      return !isNaN(n) && n === 0;
    }
    return false;
  }

  function applyState() {
    const ok = getAgreed();

    document.querySelectorAll('#CartTermsCheckbox').forEach((box) => {
      if (box.checked !== ok) box.checked = ok;
      if (ok) box.setAttribute('checked', '');
      else box.removeAttribute('checked');
    });

    document.querySelectorAll('#CartTermsError').forEach((err) => {
      err.hidden = ok;
    });

    document.querySelectorAll('.terms-agree').forEach((c) => {
      c.classList.toggle('not-accepted', !ok);
    });

    // Don't override Liquid's cart-empty disabled state — bail out if cart is empty.
    if (!isCartEmpty()) {
      document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach((b) => {
        b.disabled = !ok;
      });
    }

    document.querySelectorAll('a[href$="/checkout"]').forEach((a) => {
      a.setAttribute('aria-disabled', String(!ok));
      a.classList.toggle('is-disabled', !ok);
      a.style.pointerEvents = ok ? '' : 'none';
    });

    document.querySelectorAll(
      '.shopify-payment-button, shopify-payment-button, .additional-checkout-buttons'
    ).forEach((w) => {
      w.style.display = ok ? '' : 'none';
    });
  }

  function blockIfUnagreed(e) {
    if (getAgreed()) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    document.querySelectorAll('#CartTermsError').forEach((err) => (err.hidden = false));
    const box = document.getElementById('CartTermsCheckbox');
    if (box && typeof box.focus === 'function') box.focus();
  }

  // Show a loading spinner on the checkout target while Horizon syncs the cart and Magical Fees recalculates.
  // Safety timeout removes the class if navigation never happens (12s).
  function showLoadingOn(el) {
    if (!el) return;
    el.classList.add('t53-checkout--loading');
    setTimeout(() => el.classList.remove('t53-checkout--loading'), 12000);
  }

  document.addEventListener('change', (e) => {
    if (e.target && e.target.id === 'CartTermsCheckbox') {
      setAgreed(e.target.checked);
      applyState();
    }
  });

  // Capture phase so we run BEFORE Horizon's cart-form submit handler
  document.addEventListener(
    'click',
    (e) => {
      const target =
        e.target && e.target.closest
          ? e.target.closest('button[name="checkout"], input[name="checkout"], a[href$="/checkout"]')
          : null;
      if (!target) return;
      if (!getAgreed()) {
        blockIfUnagreed(e);
        return;
      }
      showLoadingOn(target);
    },
    true
  );

  document.addEventListener(
    'submit',
    (e) => {
      const form = e.target;
      if (!form || !form.matches || !form.matches('form[action*="/cart"]')) return;
      const submitter =
        e.submitter ||
        form.querySelector('button[type="submit"][name="checkout"]:focus') ||
        form.querySelector('button[name="checkout"]');
      if (!submitter || submitter.name !== 'checkout') return;
      if (!getAgreed()) {
        blockIfUnagreed(e);
        return;
      }
      showLoadingOn(submitter);
    },
    true
  );

  let rafId = null;
  function scheduleApply() {
    if (rafId !== null) return;
    rafId = requestAnimationFrame(() => {
      rafId = null;
      applyState();
    });
  }

  ['cart:refresh', 'cart:updated', 'cart:rendered', 'cart:items-changed', 'CartUpdate'].forEach(
    (evt) => {
      document.addEventListener(evt, () => {
        if (isCartEmpty()) clearAgreed();
        scheduleApply();
      });
    }
  );

  const observer = new MutationObserver(scheduleApply);
  function attachObserver() {
    document
      .querySelectorAll(
        'cart-drawer, cart-items-component, cart-items, .cart, [is="cart-drawer"], #cart-drawer, .cart-drawer'
      )
      .forEach((el) => {
        if (el.dataset.t53TermsObserved === '1') return;
        el.dataset.t53TermsObserved = '1';
        observer.observe(el, { childList: true, subtree: true });
      });
  }

  function bootstrap() {
    if (isCartEmpty()) clearAgreed();
    applyState();
    attachObserver();
    setTimeout(attachObserver, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bootstrap);
  } else {
    bootstrap();
  }
})();
